import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Rating,
  Alert,
  Snackbar
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { trackFormSubmission } from '../../utils/analytics';

export default function FeedbackForm() {
  const { currentUser } = useAuth();
  const [feedback, setFeedback] = useState({
    rating: 0,
    category: '',
    description: '',
    type: 'bug', // 'bug' or 'feature'
    priority: 'medium', // 'low', 'medium', 'high'
    browser: navigator.userAgent,
    url: window.location.href
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const CATEGORIES = [
    'Customer Management',
    'Estimates',
    'Job Management',
    'Scheduling',
    'Dashboard & Analytics',
    'User Interface',
    'Performance',
    'Mobile Experience',
    'Other'
  ];

  const PRIORITIES = [
    { value: 'low', label: 'Low - Nice to have' },
    { value: 'medium', label: 'Medium - Should be fixed' },
    { value: 'high', label: 'High - Critical issue' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const feedbackData = {
        ...feedback,
        userId: currentUser.uid,
        userEmail: currentUser.email,
        createdAt: serverTimestamp(),
        status: 'new'
      };

      await addDoc(collection(db, 'feedback'), feedbackData);
      trackFormSubmission('feedback', true);
      setShowSuccess(true);
      setFeedback({
        rating: 0,
        category: '',
        description: '',
        type: 'bug',
        priority: 'medium',
        browser: navigator.userAgent,
        url: window.location.href
      });
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      trackFormSubmission('feedback', false, err);
      console.error('Error submitting feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeedback(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Beta Feedback
      </Typography>
      
      <Typography variant="body1" paragraph>
        Help us improve Site Sync by sharing your feedback. Your input is valuable!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography component="legend">How would you rate your experience?</Typography>
          <Rating
            name="rating"
            value={feedback.rating}
            onChange={(event, newValue) => {
              setFeedback(prev => ({ ...prev, rating: newValue }));
            }}
          />
        </Box>

        <TextField
          select
          fullWidth
          label="Feedback Type"
          name="type"
          value={feedback.type}
          onChange={handleChange}
          SelectProps={{
            native: true
          }}
          margin="normal"
        >
          <option value="bug">Bug Report</option>
          <option value="feature">Feature Request</option>
          <option value="improvement">Improvement Suggestion</option>
        </TextField>

        <TextField
          select
          fullWidth
          label="Category"
          name="category"
          value={feedback.category}
          onChange={handleChange}
          margin="normal"
        >
          {CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </TextField>

        {feedback.type === 'bug' && (
          <TextField
            select
            fullWidth
            label="Priority"
            name="priority"
            value={feedback.priority}
            onChange={handleChange}
            margin="normal"
          >
            {PRIORITIES.map((priority) => (
              <option key={priority.value} value={priority.value}>
                {priority.label}
              </option>
            ))}
          </TextField>
        )}

        <TextField
          fullWidth
          label="Description"
          name="description"
          value={feedback.description}
          onChange={handleChange}
          multiline
          rows={4}
          margin="normal"
          required
          placeholder={
            feedback.type === 'bug' 
              ? "Please describe the issue in detail. Include steps to reproduce if possible..."
              : "Please describe your feedback in detail..."
          }
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          sx={{ mt: 3 }}
          disabled={submitting || !feedback.description}
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </form>

      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Thank you for your feedback!
        </Alert>
      </Snackbar>
    </Paper>
  );
} 